import {useCallback, useEffect, useMemo} from 'react'
import {useFormContext} from 'react-hook-form'

import {useDrawer} from '../../../components/Drawer/hooks/useDrawer'
import {useAddressActionsContext, useAddressContext} from '../../../contexts/AddressContext'
import type {AddressWidgetConfig} from '../../types'

type UseAddressWidgetKeysReturn = {
  /**
   * Закрытие `AddressDrawer` с формой настройки точки заказа
   */
  handleClose: VoidFunction
  /**
   * Открытие `AddressDrawer` с формой настройки точки заказа
   */
  handleOpen: VoidFunction
  /**
   * Обработчик отправки формы точки заказа:
   * - Если не `isFinalRoutePoint` - открытие формы настройки следующей точки заказа
   * - Если `isFinalRoutePoint` - закрытие `AddressDrawer`
   */
  handleSubmit: VoidFunction
  /**
   * Флаг, открыта ли форма настройки последней точки заказа
   */
  isFinalRoutePoint: boolean
  /**
   * Флаг, открыта ли форма настройки точки заказа
   */
  opened: boolean
}

/**
 * Возвращает коллбеки и boolean-флаги для работы с `AddressWidget`
 * @param addressName Название поля адреса в форме
 * @param contactsName Название поля контакта в форме
 * @param pointId ID точки заказа
 * @param props Конфигурация виджета `AddressWidget`
 * @returns Коллбеки и boolean-флаги для работы с `AddressWidget`
 */
export const useAddressWidget = (
  addressName: string,
  contactsName: string,
  pointId: string,
  {route_point_index}: AddressWidgetConfig,
): UseAddressWidgetKeysReturn => {
  const {open, close, opened} = useDrawer()
  const {addressIndexes, currentIndex} = useAddressContext()
  const {addAddress, changeCurrentIndex, removeAddress} = useAddressActionsContext()
  const {trigger, watch, setValue} = useFormContext()

  const isFinalRoutePoint = useMemo(
    () => addressIndexes.indexOf(route_point_index) === addressIndexes.length - 1,
    [addressIndexes, route_point_index],
  )

  function setRouteUnoptimized(): void {
    if (watch().hasOwnProperty('is_route_optimized')) {
      setValue('is_route_optimized', false)
    }
  }

  const validate = useCallback(() => {
    // Триггерим валидацию и проверяем валидность формы
    return trigger([addressName, contactsName])
  }, [addressName, contactsName, trigger])

  const handleOpen = useCallback(() => {
    changeCurrentIndex(route_point_index)
  }, [changeCurrentIndex, contactsName, pointId, route_point_index, setValue])

  const handleClose = useCallback(() => {
    changeCurrentIndex(null)
    validate()
  }, [changeCurrentIndex, validate])

  const handleSubmit = useCallback(async () => {
    setValue(`${contactsName}.pointId`, pointId)
    const isFormValid = await validate()

    if (!isFormValid) {
      return
    }

    if (isFinalRoutePoint) {
      handleClose()
      return
    }

    changeCurrentIndex(route_point_index + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addressName,
    contactsName,
    changeCurrentIndex,
    handleClose,
    isFinalRoutePoint,
    route_point_index,
    validate,
    trigger,
    pointId,
    setValue,
  ])

  useEffect(() => {
    addAddress(route_point_index)
    setValue(`${contactsName}.pointId`, pointId)
    setRouteUnoptimized()

    return () => {
      removeAddress(route_point_index)
      setRouteUnoptimized()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAddress, removeAddress, route_point_index])

  useEffect(() => {
    if (currentIndex === null) {
      close()
      return
    }

    if (currentIndex !== route_point_index) {
      close()
      return
    }

    open()
  }, [close, currentIndex, open, route_point_index])

  return {
    handleClose,
    handleOpen,
    handleSubmit,
    isFinalRoutePoint,
    opened,
  }
}
